import { resolveActiveLanguage } from '@/api/requestLanguage';
import { refreshAuthenticationSession } from '@/api/tokenRefresh';
import { environment } from '@/config/environment';
import {
  SESSION_EXPIRED_EVENT_NAME,
  clearAuthenticationTokens,
  readActiveWorkspaceSelection,
  readStoredAccessToken,
} from '@/lib/authStorage';
import { mapServerSentEventToAssistantStreamEvent } from './assistantMapping';
import type { AssistantStreamEvent } from './assistantApi.types';
import { createServerSentEventParser } from './serverSentEventParser';

const UNAUTHORIZED_STATUS = 401;

export class AssistantStreamError extends Error {
  readonly httpStatus: number;

  constructor(httpStatus: number) {
    super('assistant-stream-failed');
    this.httpStatus = httpStatus;
  }
}

function buildStreamHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': resolveActiveLanguage(),
  };
  const accessToken = readStoredAccessToken();
  if (accessToken.length > 0) {
    headers.Authorization = 'Bearer ' + accessToken;
  }
  const workspaceSelection = readActiveWorkspaceSelection();
  if (workspaceSelection.length > 0) {
    headers['X-Workspace-Id'] = workspaceSelection;
  }
  return headers;
}

function openStream(turnId: string, signal: AbortSignal): Promise<Response> {
  return fetch(environment.apiBaseUrl + '/assistant/turns/' + turnId + '/stream', {
    method: 'POST',
    headers: buildStreamHeaders(),
    signal,
  });
}

async function refreshOrExpireSession(): Promise<void> {
  try {
    await refreshAuthenticationSession();
  } catch (refreshError) {
    clearAuthenticationTokens();
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT_NAME));
    throw refreshError;
  }
}

async function openStreamWithRefresh(turnId: string, signal: AbortSignal): Promise<Response> {
  const firstResponse = await openStream(turnId, signal);
  if (firstResponse.status !== UNAUTHORIZED_STATUS) {
    return firstResponse;
  }
  void discardBody(firstResponse);
  await refreshOrExpireSession();
  return openStream(turnId, signal);
}

async function discardBody(response: Response): Promise<void> {
  if (response.body === null) {
    return;
  }
  try {
    await response.body.cancel();
  } catch (cancelError) {
    void cancelError;
  }
}

function parseEventData(rawData: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(rawData);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch (parseError) {
    if (parseError instanceof SyntaxError) {
      return null;
    }
    throw parseError;
  }
}

export async function streamAssistantTurn(
  turnId: string,
  onEvent: (event: AssistantStreamEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const response = await openStreamWithRefresh(turnId, signal);
  if (response.ok === false || response.body === null) {
    void discardBody(response);
    throw new AssistantStreamError(response.status);
  }
  const parser = createServerSentEventParser(function handleEvent(serverSentEvent) {
    const parsedData = parseEventData(serverSentEvent.data);
    if (parsedData === null) {
      return;
    }
    const streamEvent = mapServerSentEventToAssistantStreamEvent(
      serverSentEvent.eventName,
      parsedData
    );
    if (streamEvent !== null) {
      onEvent(streamEvent);
    }
  });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let readResult = await reader.read();
  while (readResult.done === false) {
    parser.pushChunk(decoder.decode(readResult.value, { stream: true }));
    readResult = await reader.read();
  }
  parser.pushChunk(decoder.decode());
  parser.finish();
}
